import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import Swal from "sweetalert2";

// Teletorget — Send kvittering (KOMPAKT)
// Krav:
// - Framer Motion modal
// - Vertikalt + horisontalt sentrert (desktop)
// - Header fixed (shrink-0)
// - Body: max-h-[85vh] + overflow-y-auto
// - Drawer på mobil
// - ESC for å lukke

const cn = (...classes: Array<string | false | undefined>) =>
    classes.filter(Boolean).join(" ");

function isEmail(s: string) {
    return /.+@.+\..+/.test(s.trim());
}

function Pill({ children }: { children: React.ReactNode }) {
    return (
        <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-2.5 py-0.5 text-[11px] font-semibold text-slate-700">
            {children}
        </span>
    );
}

function Input(props: React.InputHTMLAttributes<HTMLInputElement>) {
    return (
        <input
            {...props}
            className={cn(
                "w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none",
                "focus:ring-2 focus:ring-slate-900/10",
                props.readOnly ? "bg-slate-50 text-slate-700" : "",
                props.className
            )}
        />
    );
}

function LockedRow({ label, value }: { label: string; value: string }) {
    return (
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
            <div className="text-[11px] font-semibold text-slate-600">{label}</div>
            <div className="mt-0.5 truncate text-sm font-semibold text-slate-900">{value}</div>
        </div>
    );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <div>
            <label className="text-xs font-semibold text-slate-600">{label}</label>
            <div className="mt-1">{children}</div>
        </div>
    );
}
type ReceiptModalProps = {
    open: boolean,
    setOpen: (check: boolean) => void,
    baseUrl: string,
    currentUser: any,
    client: any,
    selectedCall: any,
    customer: any,
}
export default function ReceiptModal({ open, setOpen, baseUrl, currentUser, client, selectedCall, customer }: ReceiptModalProps) {

    const name = customer?.name;
    const phone = customer?.country + " " + customer?.phone;
    const email = customer?.email;
    const uid = selectedCall?.agent_id;
    const paymentRef = selectedCall?.payment_intent_id ? selectedCall?.payment_intent_id : selectedCall?.reference;
    const product = selectedCall?.cid;

    const method = selectedCall?.payment_intent_id ? "Stripe" : "Vipps";

    const [to, setTo] = useState(email);
    const [useCc, setUseCc] = useState(false);
    const [useBcc, setUseBcc] = useState(false);
    const [cc, setCc] = useState("");
    const [bcc, setBcc] = useState("");
    const subject = paymentRef;
    const [loading, setLoading] = useState(false);

    const canSend = useMemo(() => isEmail(to) && !loading, [to, loading]);

    useEffect(() => {
        function onKey(e: KeyboardEvent) {
            if (e.key === "Escape") setOpen(false);
        }
        if (open) window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open]);

    const handleSendClick = async () => {
        try {
            setLoading(true);

            let formData = new FormData();

            formData.append('token', currentUser?.token);
            formData.append("name", name);
            formData.append("to", to);
            formData.append('cc', cc);
            formData.append('bcc', bcc);
            formData.append('uid', selectedCall.cid);
            formData.append('agent_name', selectedCall.agent.name);
            formData.append("method", method);
            formData.append("paymentReference", paymentRef);
            formData.append("status", selectedCall.call_status);

            const response = await axios.post(baseUrl + '/sendCallReceipt', formData);

            if (response.data.status === "OK") {
                Swal.fire({
                    title: 'Suksess',
                    icon: 'success',
                    text: response.data.data,
                    timer: 1500,
                });
                setOpen(false);
            } else {
                Swal.fire({
                    title: 'Error',
                    icon: 'error',
                    text: response.data.data,
                })
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        function handleEsc(event: any) {
            if (event.key === "Escape") setOpen(false);
        }
        document.addEventListener("keydown", handleEsc);
    }, [])

    return (
        <div className="min-h-screen bg-slate-50">
            <AnimatePresence>
                {open && (
                    <>
                        {/* Backdrop */}
                        <motion.div
                            className="fixed inset-0 z-40 bg-black/40"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => !loading && setOpen(false)}
                        />

                        {/* Modal (centered on all screens) */}
                        <motion.div
                            className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4"
                            initial={{ scale: 0.96, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.96, opacity: 0 }}
                        >
                            <div
                                className={cn(
                                    "w-full sm:max-w-xl",
                                    "bg-white shadow-2xl",
                                    "rounded-t-2xl sm:rounded-2xl",
                                    "flex flex-col",
                                    "max-h-[85vh]"
                                )}
                            >
                                {/* Header (fixed) */}
                                <div className="shrink-0 flex items-start justify-between gap-3 border-b border-slate-100 bg-sky-50 px-4 py-3">
                                    <div className="min-w-0">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <h2 className="text-base font-semibold text-sky-800">Send kvittering</h2>
                                            <Pill>
                                                <span className="font-mono">{paymentRef}</span>
                                            </Pill>
                                        </div>
                                        <p className="mt-0.5 text-xs text-slate-500">Låste felt fra historikk</p>
                                    </div>
                                    <button
                                        className="rounded-xl p-2 text-slate-500 hover:bg-white/60"
                                        onClick={() => setOpen(false)}
                                    >
                                        ✕
                                    </button>
                                </div>

                                {/* Scrollable body */}
                                <div className="flex-1 overflow-y-auto px-4 py-3">
                                    <div className="grid gap-2 sm:grid-cols-2">
                                        <LockedRow label="Navn" value={name} />
                                        <LockedRow label="Telefon" value={phone} />
                                        <LockedRow label="E-post" value={email} />
                                        <LockedRow label="UID" value={uid} />
                                    </div>

                                    <div className="mt-2 grid gap-2 sm:grid-cols-2">
                                        <LockedRow label="Betalingsreferanse" value={paymentRef} />
                                        <LockedRow label="Produkt" value={product} />
                                    </div>

                                    <div className="mt-3 rounded-xl border border-slate-200 bg-white p-3">
                                        <div className="flex items-center justify-between">
                                            <div className="text-sm font-semibold text-slate-900">Mottakere</div>
                                            <div className="flex items-center gap-3">
                                                <label className="flex items-center gap-2 text-xs font-semibold">
                                                    <input type="checkbox" checked={useCc} onChange={(e) => setUseCc(e.target.checked)} /> CC
                                                </label>
                                                <label className="flex items-center gap-2 text-xs font-semibold">
                                                    <input type="checkbox" checked={useBcc} onChange={(e) => setUseBcc(e.target.checked)} /> BCC
                                                </label>
                                            </div>
                                        </div>

                                        <div className="mt-2 grid gap-2">
                                            <Field label="Til (E-post)">
                                                <Input value={to} onChange={(e) => setTo(e.target.value)} />
                                            </Field>
                                            {useCc && (
                                                <Field label="CC">
                                                    <Input value={cc} onChange={(e) => setCc(e.target.value)} />
                                                </Field>
                                            )}
                                            {useBcc && (
                                                <Field label="BCC">
                                                    <Input value={bcc} onChange={(e) => setBcc(e.target.value)} />
                                                </Field>
                                            )}
                                        </div>
                                    </div>

                                    <div className="mt-2">
                                        <LockedRow label="Emne " value={subject} />
                                    </div>
                                </div>

                                {/* Footer */}
                                <div className="shrink-0 flex items-center justify-between gap-3 border-t border-slate-100 px-4 py-3">
                                    <button
                                        className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold"
                                        onClick={() => setOpen(false)}
                                    >
                                        Avbryt
                                    </button>
                                    <button
                                        onClick={handleSendClick}
                                        disabled={!canSend}
                                        className={cn(
                                            "rounded-xl px-3 py-2 text-sm font-semibold text-white",
                                            canSend ? "bg-slate-900" : "bg-slate-300"
                                        )}
                                    >
                                        Send
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </div>
    );
}
